import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'

import Anchor from '@/components/ui/Anchor'
import Container from '@/components/ui/Container'
import Icon from '@/components/ui/Icon'
import Logo from '@/components/ui/Logo'
import { useData, useLocale } from '@/hooks/useLocale'

/**
 * Fixed 96px header with no background of its own. Its text and logo are white
 * over the hero photo and turn dark once the hero has scrolled away. It slides
 * out of view while you scroll down and back in as soon as you scroll up; at
 * the top of the page it always shows. Scroll state goes straight to the DOM as
 * data attributes, so scrolling never re-renders React. Pages leave a 96px gap
 * at the top of their hero for it.
 *
 * Below 1024px the nav folds into a full-screen menu built on the native
 * popover API, which gives Escape-to-close and top-layer stacking without state.
 * The round button beside it switches between English and Arabic. Up to 1120px
 * the links sit closer, so all six fit beside the logo and buttons.
 */
export default function Header() {
  const { ACTIONS, LANGUAGE, MENU, NAV_LINKS } = useData()
  const { toggle } = useLocale()
  const header = useRef(null)
  const menu = useRef(null)
  const closeMenu = () => menu.current?.hidePopover()

  useEffect(() => {
    const el = header.current
    let lastY = window.scrollY
    const onScroll = () => {
      const y = window.scrollY
      // White while over the hero photo; dark once the header's middle (where
      // its text sits, 48px down) has passed the bottom of the hero.
      const hero = document.querySelector('main > section')
      const heroEnd = hero?.offsetHeight ?? 0
      // A scroll-played hero (Home) turns white near its end: from the share of
      // its run named in `data-light-from`, go dark there too.
      const lightFrom = Number(hero?.dataset.lightFrom)
      const lit =
        lightFrom > 0 && y / Math.max(1, heroEnd - window.innerHeight) > lightFrom
      el.toggleAttribute('data-scrolled', y + 48 > heroEnd || lit)
      // A few pixels of slack so a trackpad's jitter doesn't flicker it.
      if (Math.abs(y - lastY) < 4) return
      el.toggleAttribute('data-hidden', y > lastY && y > 96)
      lastY = y
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <>
      <header
        ref={header}
        className="group fixed inset-x-0 top-0 z-90 transition-[translate] duration-300 data-hidden:-translate-y-full"
      >
        <Container className="flex h-24 items-center gap-3">
          <Link to="/" className="flex items-center">
            <Logo className="h-13.5 transition-[filter] duration-300 group-data-scrolled:filter-none" />
          </Link>

          <nav className="hidden min-w-0 flex-1 justify-center lg:flex">
            <div className="flex rounded-full bg-white/10 p-1.5 whitespace-nowrap inset-ring inset-ring-white/18 backdrop-blur-md transition-[background-color,box-shadow] duration-300 group-data-scrolled:bg-white/40 group-data-scrolled:inset-ring-[rgba(20,30,50,.08)] min-[70rem]:gap-1">
              {NAV_LINKS.map((link) => (
                <Anchor
                  key={link.label}
                  href={link.href}
                  className="flex h-9 items-center rounded-full px-2 text-[13px] font-medium text-white transition-colors duration-300 group-data-scrolled:text-[#1e2330] hover:bg-[rgba(127,140,160,.16)] min-[70rem]:px-3.5 min-[70rem]:text-sm"
                >
                  {link.label}
                </Anchor>
              ))}
            </div>
          </nav>

          <div className="ms-auto flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={toggle}
              lang={LANGUAGE.lang}
              aria-label={LANGUAGE.title}
              title={LANGUAGE.title}
              className="grid size-11 shrink-0 cursor-pointer place-items-center rounded-full bg-white/10 text-[15px] font-semibold text-white inset-ring inset-ring-white/18 backdrop-blur-md transition-[background-color,box-shadow,color] duration-300 group-data-scrolled:bg-white/40 group-data-scrolled:text-[#1e2330] group-data-scrolled:inset-ring-[rgba(20,30,50,.08)] hover:bg-white/24 group-data-scrolled:hover:bg-white/80 [&:lang(ar)]:pb-1"
            >
              {LANGUAGE.label}
            </button>
            {/* Below 600px the menu carries this button, leaving room in the bar. */}
            <Anchor
              href={ACTIONS.start.href}
              className="hidden h-11 items-center rounded-full bg-white px-5 text-sm font-semibold whitespace-nowrap text-ink transition-colors duration-300 group-data-scrolled:bg-brand group-data-scrolled:text-white hover:opacity-90 sm:flex"
            >
              {ACTIONS.start.label}
            </Anchor>
            <button
              type="button"
              popoverTarget="site-menu"
              aria-label={MENU.open}
              className="grid size-11 shrink-0 cursor-pointer place-items-center rounded-full bg-white/10 text-white inset-ring inset-ring-white/18 backdrop-blur-md transition-[background-color,box-shadow,color] duration-300 group-data-scrolled:bg-white/40 group-data-scrolled:text-[#1e2330] group-data-scrolled:inset-ring-[rgba(20,30,50,.08)] lg:hidden"
            >
              <Icon name="menu" size={18} stroke={2.2} />
            </button>
          </div>
        </Container>
      </header>

      <div
        ref={menu}
        id="site-menu"
        popover="auto"
        className="inset-0 m-0 size-full max-h-none max-w-none flex-col overflow-y-auto border-0 bg-ink/55 p-0 text-white backdrop-blur-[18px] open:flex"
      >
        <div className="flex h-17 shrink-0 items-center justify-between px-gutter">
          <Logo className="h-10" />
          <button
            type="button"
            popoverTarget="site-menu"
            popoverTargetAction="hide"
            aria-label={MENU.close}
            className="grid size-[42px] cursor-pointer place-items-center rounded-full bg-white/16 text-white"
          >
            <Icon name="close" size={18} stroke={2.2} />
          </button>
        </div>

        <nav className="flex flex-1 flex-col justify-center-safe gap-1 px-gutter">
          {NAV_LINKS.map((link) => (
            <Anchor
              key={link.label}
              href={link.href}
              onClick={closeMenu}
              className="flex items-center justify-between border-b border-white/12 py-3.5 text-[clamp(22px,min(7vw,5svh),40px)] tracking-[-0.02em] text-white hover:text-sky"
            >
              {link.label}
              <Icon name="arrowUpRight" size={20} className="opacity-60" />
            </Anchor>
          ))}
        </nav>

        <div className="flex shrink-0 px-gutter pt-6 pb-8">
          <Anchor
            href={ACTIONS.start.href}
            className="flex h-[50px] flex-1 items-center justify-center rounded-full bg-white text-[15px] font-semibold text-[#1e2330] hover:bg-brand-tint"
          >
            {ACTIONS.start.label}
          </Anchor>
        </div>
      </div>
    </>
  )
}
